import { cn } from "@/lib/utils";

interface ProfitLabLogoProps {
  size?: "sm" | "md" | "lg";
  showText?: boolean;
  className?: string;
}

const sizeMap = {
  sm: { mark: "h-8 w-8", text: "text-sm", sub: "text-[10px]" },
  md: { mark: "h-10 w-10", text: "text-lg", sub: "text-xs" },
  lg: { mark: "h-14 w-14", text: "text-2xl", sub: "text-sm" },
};

export function ProfitLabMark({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "relative inline-flex items-center justify-center rounded-[10px] bg-card ring-1 ring-primary/30 overflow-hidden shadow-[0_14px_36px_-18px_hsl(var(--primary)/0.8)]",
        className,
      )}
      aria-hidden="true"
    >
      <span className="absolute inset-0 bg-[radial-gradient(circle_at_28%_18%,hsl(var(--accent)/0.42),transparent_34%),radial-gradient(circle_at_78%_82%,hsl(var(--primary)/0.46),transparent_38%)]" />
      <span className="absolute inset-[1px] rounded-[9px] bg-background/78" />
      <svg viewBox="0 0 64 64" className="relative h-[84%] w-[84%]" role="img" aria-label="ProfitLab 360 mark">
        <defs>
          <linearGradient id="pl360-orbit" x1="11" y1="10" x2="55" y2="55" gradientUnits="userSpaceOnUse">
            <stop stopColor="hsl(var(--accent))" />
            <stop offset="0.52" stopColor="hsl(var(--info))" />
            <stop offset="1" stopColor="hsl(var(--primary))" />
          </linearGradient>
          <linearGradient id="pl360-core" x1="18" y1="15" x2="48" y2="50" gradientUnits="userSpaceOnUse">
            <stop stopColor="hsl(var(--foreground))" />
            <stop offset="0.45" stopColor="hsl(var(--primary))" />
            <stop offset="1" stopColor="hsl(var(--warning))" />
          </linearGradient>
        </defs>

        <path d="M12 32c0-11.05 8.95-20 20-20 6.1 0 11.56 2.73 15.23 7.03" fill="none" stroke="url(#pl360-orbit)" strokeWidth="3.6" strokeLinecap="round" />
        <path d="M52 32c0 11.05-8.95 20-20 20-6.28 0-11.88-2.9-15.55-7.43" fill="none" stroke="url(#pl360-orbit)" strokeWidth="3.6" strokeLinecap="round" opacity="0.72" />
        <path d="M15 22h34M15 42h34M22 15v34M42 15v34" stroke="hsl(var(--foreground))" strokeWidth="1" opacity="0.12" strokeLinecap="round" />
        <path
          d="M21 46V18h15.2c6.1 0 10.4 3.88 10.4 9.42 0 5.67-4.32 9.48-10.4 9.48H30v9.1h-9Zm9-16.45h5.4c1.76 0 2.92-.9 2.92-2.13 0-1.3-1.16-2.17-2.92-2.17H30v4.3Z"
          fill="url(#pl360-core)"
        />
        <path d="M30 46 45.8 18h5.7L35.8 46H30Z" fill="hsl(var(--background))" opacity="0.92" />
        <path d="M33.8 46 49.6 18h3.1L36.9 46h-3.1Z" fill="url(#pl360-core)" opacity="0.95" />
        <circle cx="49.8" cy="20" r="3.3" fill="hsl(var(--warning))" />
        <circle cx="14.8" cy="44.4" r="2.7" fill="hsl(var(--info))" />
      </svg>
    </span>
  );
}

export function ProfitLabLogo({ size = "md", showText = true, className }: ProfitLabLogoProps) {
  const s = sizeMap[size];
  return (
    <div className={cn("flex items-center gap-2.5", className)}>
      <ProfitLabMark className={s.mark} />
      {showText && (
        <div className="min-w-0 leading-tight">
          <p className={cn("font-display font-bold text-foreground tracking-normal truncate", s.text)}>
            ProfitLab<span className="gradient-text">360</span>
          </p>
          {size !== "sm" && <p className={cn("text-muted-foreground truncate", s.sub)}>AI POD Intelligence Studio</p>}
        </div>
      )}
    </div>
  );
}
